import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, Calendar, MapPin, CheckCircle2, Circle, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatSurface } from "@/lib/utils";
import Logo from "@/components/Logo";

/**
 * Vue lecture seule de l'horaire complet d'un événement (avec staff booké).
 * Aucune authentification, contrôlée par un token de partage privé.
 * URL : /share/horaire/:token
 */
export default function PublicScheduleShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data: res } = await api.get(`/public/share/event/${token}`);
        if (alive) {
          setData(res);
          setError("");
        }
      } catch (e) {
        if (alive) setError(formatApiError(e.response?.data?.detail) || "Erreur");
      }
    };
    fetchOnce();
    const interval = setInterval(fetchOnce, 30000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [token]);

  if (error) {
    return (
      <Centered>
        <AlertTriangle size={40} strokeWidth={2} className="mx-auto mb-3 text-[var(--muted)]" />
        <h2 className="font-serif text-3xl mb-2">Lien invalide</h2>
        <p className="text-sm text-[var(--muted)]">
          Ce lien est invalide ou a été révoqué par le gestionnaire.
        </p>
      </Centered>
    );
  }
  if (!data) {
    return (
      <Centered>
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">
          Chargement…
        </p>
      </Centered>
    );
  }

  return <ScheduleView data={data} />;
}

function ScheduleView({ data }) {
  const event = data.event || {};
  const officials = useMemo(() => data.officials || [], [data.officials]);
  const confirmations = useMemo(() => data.confirmations || [], [data.confirmations]);

  const officialName = useMemo(() => {
    const map = new Map();
    for (const o of officials) {
      map.set(o.id, `${o.first_name || ""} ${o.last_name || ""}`.trim() || "—");
    }
    return map;
  }, [officials]);

  // Confirmation lookup: key = `${gameId}__${officialId}__${role}`
  const confirmedSet = useMemo(() => {
    const s = new Set();
    for (const c of confirmations) {
      if (c.game_id && c.official_id) {
        s.add(`${c.game_id}__${c.official_id}__${c.role || ""}`);
      }
    }
    return s;
  }, [confirmations]);

  const allGames = (event.games || []).filter((g) => !g.is_break);
  const dates = useMemo(() => {
    const ds = new Set(allGames.map((g) => g.date).filter(Boolean));
    return [...ds].sort();
  }, [allGames]);

  const [selectedDate, setSelectedDate] = useState(null);
  const effectiveDate = selectedDate || dates[0] || null;

  const gamesForDate = useMemo(() => {
    return allGames
      .filter((g) => (effectiveDate ? g.date === effectiveDate : true))
      .sort((a, b) =>
        (a.start_time || "").localeCompare(b.start_time || "") ||
        String(a.surface || "").localeCompare(String(b.surface || ""))
      );
  }, [allGames, effectiveDate]);

  // Build a flat staff roster summary (counts per official)
  const staffSummary = useMemo(() => {
    const counts = new Map();
    for (const g of allGames) {
      for (const f of ["referee1_id", "referee2_id", "scorer_id"]) {
        const oid = g[f];
        if (!oid) continue;
        const cur = counts.get(oid) || { id: oid, name: officialName.get(oid) || "—", referee: 0, scorer: 0 };
        if (f === "scorer_id") cur.scorer += 1;
        else cur.referee += 1;
        counts.set(oid, cur);
      }
    }
    return [...counts.values()].sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [allGames, officialName]);

  return (
    <div className="min-h-screen bg-[#F5F5F0] p-4 md:p-8" data-testid="public-share-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Logo size={36} />
          <span
            className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded border border-black/70 bg-black text-white inline-flex items-center gap-1"
            data-testid="public-share-readonly-badge"
          >
            <Lock size={12} strokeWidth={2.4} /> Lecture seule
          </span>
        </div>

        <div className="card-flat mb-6">
          <div className="label mb-1">Horaire partagé</div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight mb-3" data-testid="public-share-event-name">
            {event.name || "Événement"}
          </h1>
          <div className="text-sm text-[var(--text)] space-y-1">
            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin size={14} strokeWidth={2.2} className="text-[var(--muted)]" />
                <span>{event.location}</span>
              </div>
            )}
            {(event.start_date || event.end_date) && (
              <div className="flex items-center gap-2">
                <Calendar size={14} strokeWidth={2.2} className="text-[var(--muted)]" />
                <span>
                  {event.start_date}
                  {event.end_date && event.end_date !== event.start_date ? ` → ${event.end_date}` : ""}
                </span>
              </div>
            )}
            <div className="text-xs text-[var(--muted)] pt-2">
              {allGames.length} partie{allGames.length > 1 ? "s" : ""} · {staffSummary.length} officiel
              {staffSummary.length > 1 ? "s" : ""} assigné{staffSummary.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {dates.length > 1 && (
          <div className="mb-4 flex flex-wrap gap-2" data-testid="public-share-date-tabs">
            {dates.map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDate(d)}
                className={`px-3 py-1.5 text-xs font-semibold border ${
                  effectiveDate === d
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-[var(--border-strong)] hover:bg-[var(--surface-3)]"
                }`}
                data-testid={`public-share-date-${d}`}
              >
                {d} · {allGames.filter((g) => g.date === d).length}
              </button>
            ))}
          </div>
        )}

        <div className="card-flat mb-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="public-share-table">
            <thead>
              <tr className="border-b border-black">
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Heure</th>
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Surface</th>
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Cat.</th>
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Équipes</th>
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Arbitre 1</th>
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Arbitre 2</th>
                <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-wider">Marqueur</th>
              </tr>
            </thead>
            <tbody>
              {gamesForDate.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-6 text-center text-[var(--muted)]">
                    Aucune partie pour cette journée.
                  </td>
                </tr>
              )}
              {gamesForDate.map((g) => (
                <tr
                  key={g.id}
                  className="border-b border-[var(--border)] last:border-0 align-top"
                  data-testid={`public-share-game-${g.id}`}
                >
                  <td className="py-2 px-2 tabular whitespace-nowrap">
                    {g.start_time}–{g.end_time}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">{formatSurface(g.surface)}</td>
                  <td className="py-2 px-2">{g.category || "—"}</td>
                  <td className="py-2 px-2">
                    <div className="font-semibold leading-tight">
                      {g.team_a || "—"} <span className="text-[var(--muted)]">vs</span> {g.team_b || "—"}
                    </div>
                    {g.phase_name && (
                      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-0.5">
                        {g.phase_name}
                      </div>
                    )}
                  </td>
                  <OfficialCell
                    officialId={g.referee1_id}
                    name={officialName.get(g.referee1_id)}
                    confirmed={confirmedSet.has(`${g.id}__${g.referee1_id}__referee1`)}
                  />
                  <OfficialCell
                    officialId={g.referee2_id}
                    name={officialName.get(g.referee2_id)}
                    confirmed={confirmedSet.has(`${g.id}__${g.referee2_id}__referee2`)}
                  />
                  <OfficialCell
                    officialId={g.scorer_id}
                    name={officialName.get(g.scorer_id)}
                    confirmed={confirmedSet.has(`${g.id}__${g.scorer_id}__scorer`)}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {staffSummary.length > 0 && (
          <div className="card-flat mb-6" data-testid="public-share-staff-summary">
            <div className="label mb-3">Récapitulatif staff ({staffSummary.length})</div>
            <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
              {staffSummary.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium truncate">{s.name}</span>
                  <span className="text-xs text-[var(--muted)] tabular whitespace-nowrap">
                    {s.referee > 0 && <>{s.referee} arbitre{s.referee > 1 ? "s" : ""}</>}
                    {s.referee > 0 && s.scorer > 0 && " · "}
                    {s.scorer > 0 && <>{s.scorer} marqueur{s.scorer > 1 ? "s" : ""}</>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center text-[10px] uppercase tracking-widest text-[var(--muted)] mt-8 pb-6">
          Vue lecture seule · les modifications nécessitent un accès gestionnaire
        </div>
      </div>
    </div>
  );
}

function OfficialCell({ officialId, name, confirmed }) {
  if (!officialId) {
    return <td className="py-2 px-2 text-[var(--muted)]">—</td>;
  }
  return (
    <td className="py-2 px-2">
      <div className="flex items-center gap-1.5 min-w-0">
        {confirmed ? (
          <CheckCircle2 size={13} strokeWidth={2.4} className="shrink-0" />
        ) : (
          <Circle size={13} strokeWidth={2.4} className="shrink-0 text-[var(--muted)]" />
        )}
        <span className="truncate">{name || "—"}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-0.5">
        {confirmed ? "Confirmé" : "En attente"}
      </div>
    </td>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6">
      <div className="max-w-md text-center">{children}</div>
    </div>
  );
}
